/**
 * Score a finished take held in an AudioBuffer — the solo karaoke path.
 *
 * Kept in its own `.mjs` rather than inside `liveScorer.js` so the Node test harness
 * can import it. `liveScorer.js` reaches for AudioWorklet and MediaStream, which do
 * not exist outside a browser, so anything left in there is untestable by
 * construction — and this is the path that produces the grade shown on `/fs`.
 *
 * Takes anything shaped like an AudioBuffer: `{ sampleRate, getChannelData(0) }`.
 */
import { TRACK, downsampleTo16k, bandpass, trackFrames } from './pitchTrack.mjs';
import { scoreTake } from './scoring.mjs';

/**
 * @param contour  the song's reference, from scripts/extract-contour.mjs
 * @param buffer   AudioBuffer-like holding the singer's vocal, no backing track
 * @param leadSec  the count-in. The studio starts capturing before the music does,
 *                 so the head of the buffer sits before song time zero; failing to
 *                 drop it lands every frame against the wrong part of the reference.
 */
export function scoreVocalBuffer({ contour, buffer, leadSec = 0 }) {
  if (!contour || !buffer) return null;
  const raw = buffer.getChannelData(0);
  const skip = Math.max(0, Math.round(leadSec * buffer.sampleRate));
  if (skip >= raw.length) return null;

  const signal = bandpass(
    downsampleTo16k(raw.subarray(skip), buffer.sampleRate),
    TRACK.sampleRate,
  );
  const frames = trackFrames(signal);
  const result = scoreTake(contour, frames, { startSec: 0 });

  let peak = 0;
  for (let i = 0; i < raw.length; i += 8) peak = Math.max(peak, Math.abs(raw[i]));

  return {
    ...result,
    detail: {
      durationSec: Number(((raw.length - skip) / buffer.sampleRate).toFixed(1)),
      voicedRatio: result.voicedRatio,
      peak: Number(peak.toFixed(4)),
    },
  };
}
